import axios from 'axios';
import { syncAllowedPagesCookie } from '../authCookies';

/**
 * Subscribe a shop to a plan it does not yet have.
 *
 * Lifted out of Pricing.jsx (2026-08-21) so the account-setup wizard's plan
 * step and the Pricing page run the SAME payment path. A second hand-written
 * Razorpay flow is how the two drift apart, and in August 2026 they did.
 *
 * A free plan opens no sheet at all (2026-08-27): the server activates it
 * outright, with no Razorpay object and no card on file.
 *
 * This covers the FRESH subscribe only. Upgrades between plans stay in
 * Pricing.jsx: `POST /create` refuses a shop that already has an active
 * subscription, and this reports that back as `has_active_subscription` so the
 * caller can route to its own upgrade flow.
 *
 * Resolves — never rejects — with one of:
 *   { status: 'activated', isFree, oneTime? }
 *   { status: 'cancelled' }                     user dismissed the payment sheet
 *   { status: 'has_active_subscription' }
 *   { status: 'failed', message }
 *
 * `notify` is the caller's toast surface, so the wizard and the Pricing page
 * can word things differently without this file knowing about either.
 */
export async function subscribeToPlan({
    plan,
    appUrl,
    userEmail,
    shopDomain,
    notify = { success() {}, error() {}, info() {} },
}) {
    if (!plan) return { status: 'failed', message: 'No plan selected' };

    try {
        // A free plan has its own endpoint, and takes it unconditionally.
        //
        // `/create` refuses a shop that already has a subscription and
        // `/upgrade` refuses the plan you already hold, so the ordinary case —
        // pressing the free plan's button on an account already provisioned
        // onto it — had no path through either. `/activate-free` is idempotent
        // and opens no checkout: pressing it twice, or while already
        // subscribed, is success both times.
        if (Number(plan.price) === 0) {
            const free = await axios.post(`${appUrl}/api/subscriptions/activate-free`, {
                plan_id: plan._id,
                shop_domain: shopDomain,
            }, { withCredentials: true });

            if (!free.data.success) {
                return { status: 'failed', message: free.data.message || 'Could not activate this plan' };
            }
            // The server granted the plan's pages in that same request; refresh
            // the cookie the sidebar actually renders from.
            await syncAllowedPagesCookie(appUrl, shopDomain);
            return {
                status: 'activated',
                isFree: true,
                alreadyActive: free.data.data?.already_active === true,
                subscription: free.data.data?.subscription || null,
            };
        }

        const response = await axios.post(`${appUrl}/api/subscriptions/create`, {
            plan_id: plan._id,
            customer_email: userEmail,
            shop_domain: shopDomain,
        }, { withCredentials: true });

        if (!response.data.success) {
            return { status: 'failed', message: response.data.message || 'Failed to create subscription' };
        }

        const { razorpay_subscription_id, razorpay_key_id, is_free_plan, subscription_id } = response.data.data;

        // ===== free-plan special case =====
        // Only reachable when the SERVER says free and the caller thought
        // otherwise — a stale price in the caller's hands. The server is right.
        if (is_free_plan) {
            // Backend already synced features to the DB in this same request;
            // refresh the cookie so the UI agrees. Deliberately the non-reloading
            // variant — the caller decides where to go next.
            await syncAllowedPagesCookie(appUrl, shopDomain);
            return {
                status: 'activated',
                isFree: true,
                subscription: response.data.data.subscription || null,
            };
        }

        // ===== paid plan flow =====
        if (!razorpay_subscription_id) {
            return { status: 'activated', isFree: false };
        }

        return await openSubscriptionCheckout({
            plan, appUrl, userEmail, shopDomain, notify,
            razorpaySubscriptionId: razorpay_subscription_id,
            razorpayKeyId: razorpay_key_id,
            subscriptionId: subscription_id,
        });
    } catch (error) {
        console.error('Error creating subscription:', error);
        const msg = error.response?.data?.message;
        if (msg && msg.toLowerCase().includes('active subscription')) {
            return { status: 'has_active_subscription' };
        }
        return { status: 'failed', message: msg || error?.message || 'Failed to create subscription' };
    }
}

/**
 * Move a shop that ALREADY has an active subscription onto a different plan.
 *
 * Not an edge case in onboarding, which is what makes this belong here rather
 * than only on the Pricing page: `assignFreeSubscriptionForNewShop`
 * (login.js) gives every self-serve store an active subscription the moment
 * the account is created, so by the time the setup wizard reaches step 3 there
 * is always one. `POST /create` refuses that shop, and treating the refusal as
 * success is how choosing a plan silently completed setup without ever opening
 * a checkout (2026-08-21).
 *
 * Resolves with the same vocabulary as subscribeToPlan.
 */
export async function upgradeToPlan({
    plan,
    appUrl,
    userEmail,
    shopDomain,
    notify = { success() {}, error() {}, info() {} },
}) {
    if (!plan) return { status: 'failed', message: 'No plan selected' };
    if (!shopDomain) return { status: 'failed', message: 'Shop information missing. Please refresh the page or log in again.' };

    try {
        const response = await axios.post(`${appUrl}/api/subscriptions/upgrade`, {
            new_plan_id: plan._id,
            shop_domain: shopDomain,
        }, { withCredentials: true });

        if (!response.data.success) {
            return { status: 'failed', message: response.data.message || 'Failed to upgrade subscription' };
        }

        const {
            razorpay_subscription_id, razorpay_key_id, order_id, amount,
            prorated_amount, requires_payment, is_free_plan,
        } = response.data.data;

        // Moving to a free plan, or a proration that nets to nothing: there is
        // no sheet to open.
        if (is_free_plan || !requires_payment) {
            await syncAllowedPagesCookie(appUrl, shopDomain);
            return { status: 'activated', isFree: !!is_free_plan, message: response.data.message };
        }

        // Prorated difference, charged as a one-time order.
        if (order_id && amount) {
            return await new Promise((resolve) => {
                loadRazorpay(() => {
                    new window.Razorpay({
                        key: razorpay_key_id,
                        order_id,
                        amount,
                        name: 'ScaleX AI',
                        description: `Upgrade to ${plan.name}${prorated_amount != null ? ` (${currencySymbol(plan.currency)}${Number(prorated_amount).toFixed(2)})` : ''}`,
                        image: '/logo.png',
                        prefill: { email: userEmail },
                        theme: { color: '#3399cc' },
                        config: { display: PAYMENT_BLOCKS },
                        handler: async function () {
                            try {
                                await axios.post(`${appUrl}/api/subscriptions/upgrade/confirm`, {
                                    order_id,
                                    new_plan_id: plan._id,
                                    shop_domain: shopDomain,
                                }, { withCredentials: true });
                                await syncAllowedPagesCookie(appUrl, shopDomain);
                                resolve({ status: 'activated', isFree: false });
                            } catch (err) {
                                resolve({ status: 'failed', message: err.response?.data?.message || 'Upgrade confirmation failed' });
                            }
                        },
                        modal: { ondismiss: () => resolve({ status: 'cancelled' }) },
                    }).open();
                });
            });
        }

        // Upgrade that opened a brand-new recurring subscription instead.
        if (razorpay_subscription_id) {
            return await new Promise((resolve) => {
                loadRazorpay(() => {
                    new window.Razorpay({
                        key: razorpay_key_id,
                        subscription_id: razorpay_subscription_id,
                        name: 'ScaleX AI',
                        description: `Upgrade to ${plan.name}`,
                        image: 'https://s3.eu-north-1.amazonaws.com/www.cognitiveux.ai/Logo_1200_1200.png',
                        prefill: { email: userEmail },
                        theme: { color: '#3399cc' },
                        handler: async function () {
                            await syncAllowedPagesCookie(appUrl, shopDomain);
                            resolve({ status: 'activated', isFree: false });
                        },
                        modal: { ondismiss: () => resolve({ status: 'cancelled' }) },
                    }).open();
                });
            });
        }

        // Server reported success with nothing to pay and nothing to open.
        return { status: 'activated', isFree: false };
    } catch (error) {
        console.error('Error upgrading subscription:', error);
        return {
            status: 'failed',
            message: error.response?.data?.message || 'Failed to upgrade subscription',
        };
    }
}

/**
 * The recurring-mandate checkout, with the one-time-payment fallback for cards
 * that refuse auto-pay — common enough on Indian cards that dropping it would
 * turn a working purchase into a dead end.
 */
function openSubscriptionCheckout({
    plan, appUrl, userEmail, shopDomain, notify,
    razorpaySubscriptionId, razorpayKeyId, subscriptionId,
}) {
    return new Promise((resolve) => {
        loadRazorpay(() => {
            const razorpay = new window.Razorpay({
                key: razorpayKeyId,
                subscription_id: razorpaySubscriptionId,
                name: 'ScaleX AI',
                description: plan.name,
                image: '/logo.png',
                prefill: { email: userEmail },
                theme: { color: '#3399cc' },
                subscription_card_change: false,
                handler: async function () {
                    // Razorpay's webhook can lag the redirect; poll rather than
                    // trust the first read.
                    try {
                        for (let attempt = 0; attempt < 3; attempt += 1) {
                            await axios.post(`${appUrl}/api/subscriptions/sync-after-payment`,
                                { razorpay_subscription_id: razorpaySubscriptionId, shop_domain: shopDomain },
                                { withCredentials: true });
                            if (attempt < 2) {
                                await new Promise((r) => setTimeout(r, 1500));
                            }
                        }
                    } catch (e) {
                        console.warn('Sync after payment:', e);
                    }
                    await syncAllowedPagesCookie(appUrl, shopDomain);
                    resolve({ status: 'activated', isFree: false });
                },
                modal: {
                    ondismiss: async function () {
                        // Leave no half-created subscription behind, or the next
                        // attempt hits "already has an active subscription".
                        if (subscriptionId && shopDomain) {
                            try {
                                await axios.post(`${appUrl}/api/subscriptions/cancel`,
                                    { subscription_id: subscriptionId, shop_domain: shopDomain },
                                    { withCredentials: true });
                            } catch (e) {
                                console.error('Cancel subscription on dismiss failed', e);
                            }
                        }
                        resolve({ status: 'cancelled' });
                    },
                },
            });

            razorpay.on('payment.failed', async function (failureResponse) {
                const errorCode = failureResponse.error?.code;
                const errorDesc = failureResponse.error?.description || '';
                const isRecurringError = errorCode === 'BAD_REQUEST_ERROR'
                    && (errorDesc.toLowerCase().includes('recurring')
                        || errorDesc.toLowerCase().includes('emandate')
                        || errorDesc.toLowerCase().includes('not support'));

                if (!isRecurringError) {
                    resolve({ status: 'failed', message: errorDesc || 'Payment failed. Please try again.' });
                    return;
                }

                razorpay.close();
                notify.info('Your card does not support auto-pay. Proceeding with one-time payment...');

                try {
                    await axios.post(`${appUrl}/api/subscriptions/cancel`,
                        { subscription_id: subscriptionId, shop_domain: shopDomain },
                        { withCredentials: true });
                } catch (e) {
                    console.error('Cancel failed sub:', e);
                }

                try {
                    const orderResp = await axios.post(`${appUrl}/api/subscriptions/create-one-time-order`,
                        { plan_id: plan._id, shop_domain: shopDomain }, { withCredentials: true });
                    if (!orderResp.data.success) {
                        resolve({ status: 'failed', message: orderResp.data.message || 'Failed to create one-time payment' });
                        return;
                    }
                    const { order_id: fallbackOrderId, amount: fallbackAmount } = orderResp.data.data;

                    new window.Razorpay({
                        key: razorpayKeyId,
                        order_id: fallbackOrderId,
                        amount: fallbackAmount,
                        name: 'ScaleX AI',
                        description: `${plan.name} (One-time payment)`,
                        image: '/logo.png',
                        prefill: { email: userEmail },
                        theme: { color: '#3399cc' },
                        config: { display: PAYMENT_BLOCKS },
                        handler: async function (fallbackResponse) {
                            try {
                                await axios.post(`${appUrl}/api/subscriptions/confirm-one-time`, {
                                    order_id: fallbackOrderId,
                                    plan_id: plan._id,
                                    shop_domain: shopDomain,
                                    razorpay_payment_id: fallbackResponse.razorpay_payment_id,
                                }, { withCredentials: true });
                                await syncAllowedPagesCookie(appUrl, shopDomain);
                                resolve({ status: 'activated', isFree: false, oneTime: true });
                            } catch (e) {
                                resolve({ status: 'failed', message: 'Payment confirmation failed' });
                            }
                        },
                        modal: { ondismiss: () => resolve({ status: 'cancelled' }) },
                    }).open();
                } catch (e) {
                    resolve({ status: 'failed', message: e.response?.data?.message || 'Failed to create one-time payment' });
                }
            });

            razorpay.open();
        });
    });
}

/** The symbol for a plan's currency. Mirrors PlanCard's currencySymbol() deliberately —
 *  both are one line, and importing a component into a payment util to share
 *  it would be the worse coupling. */
function currencySymbol(currency) {
    return String(currency || 'INR').toUpperCase() === 'USD' ? '$' : '₹';
}

const PAYMENT_BLOCKS = {
    blocks: {
        upi: { name: 'UPI', instruments: [{ method: 'upi', flows: ['collect', 'intent', 'qr'] }] },
        cards: { name: 'Cards', instruments: [{ method: 'card' }] },
    },
    sequence: ['block.upi', 'block.cards'],
    preferences: { show_default_blocks: false },
};

/** Razorpay's script is idempotent to re-append, but re-fetching it on every
 *  attempt is a visible delay on a slow connection. */
function loadRazorpay(onReady) {
    if (window.Razorpay) {
        onReady();
        return;
    }
    const script = document.createElement('script');
    script.src = 'https://checkout.razorpay.com/v1/checkout.js';
    script.onload = onReady;
    document.body.appendChild(script);
}
